import { supabase } from '../config/supabase';

/**
 * Funnel instrumentation. Fire-and-forget by design.
 *
 * Analytics must never break, block or slow the thing it is measuring, so every
 * call here swallows its own failures. A dropped event costs a row in a chart;
 * a thrown one costs a user their action.
 *
 * Signups are not tracked — auth.users.created_at already answers that. These
 * events cover what happens afterwards.
 *
 * Read them in the SQL editor; the client has no SELECT policy on this table.
 *
 *   -- activation: what share of signups create something in their first week
 *   select date_trunc('week', u.created_at)::date as cohort,
 *          count(distinct u.id) as signups,
 *          count(distinct e.user_id) as activated
 *     from auth.users u
 *     left join product_events e
 *       on e.user_id = u.id
 *      and e.name = 'event_created'
 *      and e.created_at < u.created_at + interval '7 days'
 *    group by 1 order by 1 desc;
 *
 *   -- where onboarding loses people
 *   select props->>'step' as step, count(distinct user_id) as reached
 *     from product_events where name = 'onboarding_step'
 *    group by 1 order by 2 desc;
 */

export type EventName =
  | 'onboarding_step'        // props: { step }
  | 'onboarding_completed'
  | 'onboarding_skipped'
  | 'event_created'
  | 'space_created'
  | 'invite_sent'            // props: { role }
  | 'calendar_imported'      // props: { source: 'google' | 'ics' }
  | 'share_link_created'
  | 'limit_hit'              // props: { resource } — for the Phase 4 paid caps
  | 'upgrade_clicked';       // props: { plan, from } — for Phase 5

export function track(name: EventName, props: Record<string, unknown> = {}): void {
  // Deliberately not awaited. Callers should never have their own work delayed
  // by an analytics round trip.
  void (async () => {
    try {
      const { data } = await supabase.auth.getUser();
      const userId = data.user?.id;
      // The RLS policy requires user_id = auth.uid(), so an unauthenticated
      // write would be rejected anyway. Skip it rather than log a failure.
      if (!userId) return;

      await supabase.from('product_events').insert({ user_id: userId, name, props });
    } catch {
      // Intentionally silent. An analytics failure is not the user's problem,
      // and reporting it would put noise in Sentry for no actionable reason.
    }
  })();
}
